import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import { Heart, ChevronLeft, History as HistoryIcon, ChevronRight } from "lucide-react";
import { PILLARS } from "./practices/practiceConfigs";

export default function Practices() {
  const t = useT();
  usePageMeta(t("practices_hub_title"));
  const navigate = useNavigate();
  const [favs, setFavs] = useState(new Set());

  const loadFavs = useCallback(async () => {
    try {
      const { data } = await api.get("/favorites?kind=practice");
      setFavs(new Set((Array.isArray(data) ? data : []).map((r) => r.item_key)));
    } catch { /* offline: empty hearts */ }
  }, []);
  useEffect(() => { loadFavs(); }, [loadFavs]);

  const toggleFav = async (key, pillar) => {
    const isFav = favs.has(key);
    setFavs((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(key); else next.add(key);
      return next;
    });
    try { await api.post("/favorites/toggle", { kind: "practice", item_key: key, pillar }); }
    catch {
      setFavs((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(key); else next.delete(key);
        return next;
      });
    }
  };

  return (
    <div className="space-y-6" data-testid="practices-screen">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate("/app/deep-within")} data-testid="pr-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ChevronLeft className="h-4 w-4" /> {t("dw.title")}
        </button>
        <Link to="/app/practices/history" data-testid="pr-history-link" className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
          <HistoryIcon className="h-4 w-4" /> {t("practices_history_title")}
        </Link>
      </div>

      <PageHeader eyebrow={t("dw.title")} title={t("practices_hub_title")} subtitle={t("practices_hub_subtitle")} />

      {PILLARS.map((p) => (
        <section key={p.pillar} className="space-y-3" data-testid={`pillar-${p.pillar}`}>
          <div className="border-l-4 pl-3" style={{ borderColor: p.color }}>
            <h2 className="font-display text-xl" style={{ color: p.color }}>{t(p.titleKey)}</h2>
            <p className="text-sm italic text-muted-foreground">{t(p.descKey)}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {p.practices.map((pr) => {
              const Icon = pr.icon;
              const favored = favs.has(pr.key);
              return (
                <div key={pr.key} data-testid={`practice-${pr.key}`} className="group relative flex items-start gap-3 rounded-2xl border border-border bg-card p-4 transition-colors hover:bg-accent/30">
                  <button onClick={() => navigate(`/app/practices/${pr.key}`)} className="absolute inset-0 rounded-2xl" aria-label={t(pr.titleKey)} />
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: p.color + "22" }}>
                    <Icon className="h-5 w-5" style={{ color: p.color }} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground">{t(pr.titleKey)}</p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{t(pr.descKey)}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleFav(pr.key, p.pillar); }}
                    data-testid={`heart-practice-${pr.key}`}
                    className="relative z-10 rounded-lg p-1.5 transition-colors"
                    aria-label={favored ? t("unfavorite") : t("favorite")}
                  >
                    <Heart className="h-5 w-5" fill={favored ? "#D4647A" : "none"} style={{ color: favored ? "#D4647A" : "#9CA3AF" }} />
                  </button>
                  <ChevronRight className="relative z-10 mt-1 h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
